import {
  StakeholderType as ST,
  type StakeholderType,
  type User,
} from '../types/user'

export interface VisibilityContext {
  currentUser: User | null
  senderStakeholderType?: StakeholderType | string
  senderUserId?: number
}

/**
 * Determine if current user is the sender (creator) of the item
 */
export function isCurrentUserSender(ctx: VisibilityContext): boolean {
  if (!ctx.currentUser || !ctx.senderUserId) return false
  return ctx.currentUser.id === ctx.senderUserId
}

/**
 * Determine if Accept/Decline buttons should be shown
 * Should NOT appear for the sender - only for recipients
 */
export function shouldShowAcceptDeclineButtons(
  ctx: VisibilityContext,
): boolean {
  return !isCurrentUserSender(ctx)
}

/**
 * Get field visibility for Project Terms based on sender/recipient relationship
 */
export function getProjectFieldVisibility(ctx: VisibilityContext) {
  const isSender = isCurrentUserSender(ctx)
  const currentStakeholder = ctx.currentUser?.current_profile?.stakeholder_type

  return {
    // If sender is Broker sending to Third-Party Broker, hide Contractor fields
    showContractorFields: !(
      ctx.senderStakeholderType === 'BROKER' &&
      currentStakeholder === 'BROKER' &&
      !isSender
    ),
    // If sender is Contractor sending to Broker, hide Third-Party fields
    showThirdPartyFields: !(
      ctx.senderStakeholderType === 'CONTRACTOR' &&
      currentStakeholder === 'BROKER' &&
      !isSender
    ),
    // Sender should see all fields
    isSender,
  }
}

/**
 * Get field visibility for Dispatch based on sender/recipient relationship
 */
export function getDispatchFieldVisibility(ctx: VisibilityContext) {
  const isSender = isCurrentUserSender(ctx)

  return {
    showAllFields: isSender, // Sender sees all
    isSender,
  }
}

/**
 * Get field visibility for Project contact fields based on creator stakeholder type
 */
export function getProjectContactFieldVisibility(
  creatorStakeholderType: StakeholderType,
) {
  const isBroker = creatorStakeholderType === ST.BROKER
  return {
    showSubHauler: isBroker,
    showThirdPartyBroker: isBroker,
    showContractor: !isBroker,
    showThirdPartyContractor: !isBroker,
  }
}
